/*
===============================================================================================================
                                 Copyright 2024-2024, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { MerchantContext } from '@/contexts/merchant-context';
import { LocaleContext } from '@/contexts/locale-context';
import { generateReturnOptionsConfig } from '@/utilities/in-store-returns';
import getValue from '@/mappings/return-methods';
import { RETURN_METHOD_TYPE } from '@/utilities/return-method-types';
import InStoreReturnsBlock from './instore-return-block';
import { unEscapeCustomText } from '../page-customisation/utilities';

const CARD_VISIBILITY_MAP = {
  inStoreReturns: RETURN_METHOD_TYPE.STORE_DROP_OFF,
  outOfHomeReturns: RETURN_METHOD_TYPE.CARRIER,
};

/**
 * @typedef {Object} CardConfig
 * @property {{ enabled: boolean, text: string }} label
 * @property {{ enabled: boolean, text: string }} title
 * @property {{ enabled: boolean, text: string }} description
 * @property {{ enabled: boolean, text: string, color: string }} footer
 */

/**
 * @template {Object<string,CardConfig>} CustomTextObject
 */

/**
 * A wrapper around customisable return method cards
 * @param {object} props
 * @param {CustomTextObject} props.customTextValues - The text values for the in-store returns cards
 * @param {boolean} props.editEnabled - Whether or not the edit mode for the in-store returns block is enabled
 * @param {Function} props.onChange - A function called when a card field is updated
 * @return {JSX.Element}
 */
function EditableInStoreReturnsBlock({
  customTextValues,
  editEnabled,
  onChange,
}) {
  const { translate } = useContext(LocaleContext);
  const merchantContext = useContext(MerchantContext);
  const { getApplicationConfigValue, getReturnMethodsConfig } = merchantContext;

  const returnMethods = getReturnMethodsConfig();
  const returnOptions = generateReturnOptionsConfig({
    companyData: getValue({ context: merchantContext, translate }),
    assetsData: getApplicationConfigValue('inStoreReturnsForm') || {},
    customTextData: customTextValues,
  }).filter((option) => returnMethods[CARD_VISIBILITY_MAP[option.id]]?.visible);

  const onCardFieldChange = useCallback(
    ({ cardId, fieldId, value }) => {
      const currentValues = customTextValues?.[cardId];
      onChange({
        ...customTextValues,
        [cardId]: {
          ...currentValues,
          [fieldId]: unEscapeCustomText(value),
        },
      });
    },
    [customTextValues],
  );

  return (
    <InStoreReturnsBlock
      returnOptions={returnOptions}
      editableMode
      centered
      editEnabled={editEnabled}
      onChange={onCardFieldChange}
    />
  );
}

EditableInStoreReturnsBlock.defaultProps = {
  customTextValues: null,
  editEnabled: false,
  onChange: null,
};

EditableInStoreReturnsBlock.propTypes = {
  customTextValues: PropTypes.shape(),
  editEnabled: PropTypes.bool,
  onChange: PropTypes.func,
};

export default EditableInStoreReturnsBlock;
